import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { LANGUAGE_STORAGE_KEY } from "../../i18n";
import { getRegisterAgeList, getRegisterCategories } from "../../api/postgre/registerApi";
import Select, { components } from "react-select";
import { ChevronDown, Loader2 } from "lucide-react";
import ErrorMessage from "../Common/ErrorMessage";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-4 w-4 text-slate-400" />
    </components.DropdownIndicator>
);

const CheckboxOption = (props) => (
    <components.Option {...props}>
        <div className={`flex items-center gap-2 ${props.isDisabled ? "opacity-40" : ""}`}>
            <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
                disabled={props.isDisabled}
                className="accent-primary h-4 w-4 rounded"
            />
            <span className={`text-sm ${props.isDisabled ? "text-slate-400 line-through" : "text-slate-700"}`}>
                {props.label}
            </span>
        </div>
    </components.Option>
);

const accountSelectStyles = {
    ...defaultReactSelectStyles,
    control: (base, state) => ({
        ...defaultReactSelectStyles.control(base),
        borderRadius: "0.75rem",
        backgroundColor: "#f7f7f7",
        borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
        boxShadow: "none",
        outline: "none",
        minHeight: "48px",
        "&:hover": {
            borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
            backgroundColor: "#f7f7f7",
        },
        "&:active": {
            backgroundColor: "#f7f7f7",
        },
    }),
};

const normalizeName = (value) => String(value || "").trim();

const PersonalInfoSection = ({ control, errors, isEditing }) => {
    const { t } = useTranslation();
    const [ageOptions, setAgeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [optionsLoaded, setOptionsLoaded] = useState(false);

    useEffect(() => {
        const lang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || "en";
        Promise.all([
            getRegisterAgeList(),
            getRegisterCategories(lang),
        ]).then(([ages, cats]) => {
            setAgeOptions(ages);
            setCategoryOptions(cats);
            setOptionsLoaded(true);
        }).catch(() => setOptionsLoaded(true));
    }, []);

    if (!optionsLoaded) {
        return (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("ACCOUNT_LOADING_PROFILE")}</span>
            </div>
        );
    }

    return (
        <div>
            <p className="text-sm font-semibold text-slate-900">{t("ACCOUNT_PERSONAL_INFO_TITLE")}</p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_FIRST_NAME")}</span>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            required: t("AUTH_VALIDATION_FIRST_NAME_REQUIRED"),
                            validate: (value) => normalizeName(value) ? true : t("AUTH_VALIDATION_FIRST_NAME_REQUIRED"),
                        }}
                        render={({ field }) => (
                            <input
                                {...field}
                                onBlur={(e) => {
                                    field.onBlur();
                                    field.onChange(normalizeName(e.target.value));
                                }}
                                type="text"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.firstName?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_LAST_NAME")}</span>
                    <Controller
                        name="lastName"
                        control={control}
                        rules={{
                            required: t("AUTH_VALIDATION_LAST_NAME_REQUIRED"),
                            validate: (value) => normalizeName(value) ? true : t("AUTH_VALIDATION_LAST_NAME_REQUIRED"),
                        }}
                        render={({ field }) => (
                            <input
                                {...field}
                                onBlur={(e) => {
                                    field.onBlur();
                                    field.onChange(normalizeName(e.target.value));
                                }}
                                type="text"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.lastName?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_EMAIL_ADDRESS")}</span>
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: t("AUTH_VALIDATION_EMAIL_REQUIRED"),
                            pattern: {
                                value: /\S+@\S+\.\S+/,
                                message: t("AUTH_VALIDATION_EMAIL_INVALID"),
                            },
                        }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="email"
                                readOnly
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-400 outline-none focus:outline-none cursor-default"
                            />
                        )}
                    />
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_PHONE_NUMBER")}</span>
                    <Controller
                        name="phone"
                        control={control}
                        rules={{
                            required: t("AUTH_VALIDATION_PHONE_REQUIRED"),
                            pattern: {
                                value: /^\+?[\d\s\-().]{7,20}$/,
                                message: t("AUTH_VALIDATION_PHONE_INVALID"),
                            },
                        }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="tel"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.phone?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_AGE")}</span>
                    <Controller
                        name="age"
                        control={control}
                        render={({ field }) => (
                            isEditing ? (
                                <Select
                                    {...field}
                                    value={field.value ? (ageOptions.find((o) => o.value === String(field.value?.value || field.value)) || null) : null}
                                    options={ageOptions}
                                    placeholder={t("AUTH_PLACEHOLDER_AGE")}
                                    classNamePrefix="react-select"
                                    components={{ DropdownIndicator }}
                                    styles={accountSelectStyles}
                                    isClearable
                                />
                            ) : (
                                <div className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-400">
                                    {(() => {
                                        const val = field.value?.value || field.value;
                                        return ageOptions.find((o) => o.value === String(val))?.label || val || "—";
                                    })()}
                                </div>
                            )
                        )}
                    />
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_CATEGORIES")}</span>
                    <Controller
                        name="categories"
                        control={control}
                        render={({ field }) => (
                            isEditing ? (
                                <Select
                                    {...field}
                                    value={(field.value || []).map((v) => {
                                        const val = v.value || v;
                                        if (val === "all") return { value: "all", label: t("COMMON_ALL") };
                                        return categoryOptions.find((o) => o.value === String(val)) || { value: String(val), label: String(val) };
                                    })}
                                    isMulti
                                    options={[{ value: "all", label: t("COMMON_ALL") }, ...categoryOptions]}
                                    placeholder={t("AUTH_PLACEHOLDER_CATEGORY")}
                                    classNamePrefix="react-select"
                                    components={{ DropdownIndicator, Option: CheckboxOption }}
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                    isOptionDisabled={(option) => option.value !== "all" && (field.value || []).some((v) => v.value === "all")}
                                    onChange={(selected) => {
                                        const hasAll = (field.value || []).some((v) => v.value === "all");
                                        const selectedAll = selected.some((s) => s.value === "all");
                                        if (selectedAll && !hasAll) {
                                            field.onChange([{ value: "all", label: t("COMMON_ALL") }]);
                                        } else {
                                            field.onChange(selected.filter((s) => s.value !== "all"));
                                        }
                                    }}
                                    styles={{
                                        ...accountSelectStyles,
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: "#e8eafc",
                                            borderRadius: "999px",
                                            padding: "0 2px",
                                            flexShrink: 0,
                                            overflow: "visible",
                                        }),
                                        multiValueLabel: (base) => ({
                                            ...base,
                                            color: "#0f1b8f",
                                            fontSize: "11px",
                                            padding: "1px 4px",
                                            whiteSpace: "nowrap",
                                            overflow: "visible",
                                            textOverflow: "unset",
                                        }),
                                        multiValueRemove: (base) => ({
                                            ...base,
                                            color: "#0f1b8f",
                                            borderRadius: "999px",
                                            ":hover": { backgroundColor: "#0a1266", color: "#ffffff" },
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            flexWrap: "nowrap",
                                            overflowX: "auto",
                                            scrollbarWidth: "none",
                                            msOverflowStyle: "none",
                                            padding: "0 8px",
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isFocused && !state.isDisabled ? "#f1f5f9" : "transparent",
                                            color: state.isDisabled ? "#94a3b8" : "#334155",
                                            cursor: state.isDisabled ? "not-allowed" : "pointer",
                                        }),
                                    }}
                                />
                            ) : (
                                <div className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-400 min-h-[48px]">
                                    {Array.isArray(field.value) && field.value.length > 0
                                        ? field.value.map((c) => {
                                            const val = c.value || c;
                                            if (val === "all") return t("COMMON_ALL");
                                            return categoryOptions.find((o) => o.value === String(val))?.label || val;
                                        }).join(", ")
                                        : "—"
                                    }
                                </div>
                            )
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default PersonalInfoSection;
